import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import DashboardSidebar from '@/components/DashboardSidebar';
import { useLanguage } from '@/contexts/LanguageContext';
import { Globe, Bell, Mail, UserCircle, ArrowRight, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import kifahLogo from '@/assets/kifah-logo-clean.png';

const DashboardLayout = ({ children }: { children: React.ReactNode }) => {
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const mailRef = useRef<HTMLDivElement>(null);

  // Real data
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
      if (session) {
        setUserName(session.user.user_metadata?.full_name || '');
        loadNotifications(session.user.id);
        loadUnreadMessages(session.user.id);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
      if (session) {
        setUserName(session.user.user_metadata?.full_name || '');
        loadNotifications(session.user.id);
        loadUnreadMessages(session.user.id);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const loadNotifications = async (userId: string) => {
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .eq('is_read', false)
      .order('created_at', { ascending: false })
      .limit(5);
    setNotifications(data || []);
  };

  const loadUnreadMessages = async (userId: string) => {
    const { count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('receiver_id', userId)
      .eq('is_read', false);
    setUnreadMsgCount(count || 0);
  };

  // Realtime subscriptions
  useEffect(() => {
    const channel = supabase
      .channel('header-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, () => {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session) loadNotifications(session.user.id);
        });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, () => {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session) loadUnreadMessages(session.user.id);
        });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (mailRef.current && !mailRef.current.contains(e.target as Node)) setMailOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const unreadNotifCount = notifications.length;

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="min-h-screen flex w-full overflow-x-hidden" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div className="min-w-0 flex-1 flex flex-col">
          {/* Top Header */}
          <header className="h-14 flex items-center justify-between border-b border-border/30 bg-white/95 backdrop-blur-md px-4 gap-3 sticky top-0 z-50 shadow-sm">
            <div className="flex items-center gap-3 min-w-0">
              <SidebarTrigger className="text-foreground shrink-0" />
              <img
                src={kifahLogo}
                alt={t('شعار أكاديمية الكفاح', 'Al Kifah Academy logo')}
                className="h-8 w-auto"
                loading="eager"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* User Account */}
              <button
                onClick={() => navigate(isLoggedIn ? '/my-account' : '/auth')}
                className="flex items-center gap-1.5 text-foreground/70 hover:text-foreground text-xs transition-all hover:scale-110"
                title={isLoggedIn ? t('حسابي', 'My Account') : t('تسجيل دخول', 'Sign In')}
              >
                <UserCircle className="w-5 h-5" />
                <span className="hidden sm:inline font-medium">
                  {isLoggedIn ? (userName || t('حسابي', 'My Account')) : t('تسجيل دخول', 'Sign In')}
                </span>
              </button>

              {/* Notifications */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => { setNotifOpen(!notifOpen); setMailOpen(false); }}
                  className="relative text-foreground/70 hover:text-foreground transition-all hover:scale-110"
                  title={t('الإشعارات', 'Notifications')}
                >
                  <Bell className="w-5 h-5" />
                  {unreadNotifCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-destructive rounded-full text-[8px] text-white flex items-center justify-center font-bold animate-pulse">
                      {unreadNotifCount}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <div className={`absolute top-full mt-2 ${lang === 'ar' ? 'left-0' : 'right-0'} w-72 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200`}>
                    <div className="flex items-center justify-between px-4 py-2.5 bg-primary/5 border-b border-border">
                      <span className="font-bold text-sm text-foreground">{t('الإشعارات', 'Notifications')}</span>
                      <button onClick={() => setNotifOpen(false)} className="text-muted-foreground hover:text-foreground">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="text-center text-muted-foreground text-xs py-6">{t('لا توجد إشعارات جديدة', 'No new notifications')}</p>
                      ) : (
                        notifications.map((n) => (
                          <div key={n.id} className="px-4 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors cursor-pointer">
                            <p className="text-sm text-foreground leading-relaxed">{lang === 'ar' ? n.title_ar : n.title_en}</p>
                            <span className="text-[10px] text-muted-foreground mt-1 block">
                              {new Date(n.created_at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                    <button
                      onClick={() => { setNotifOpen(false); navigate('/notifications'); }}
                      className="w-full text-center text-xs text-primary font-bold py-2.5 hover:bg-primary/5 transition-colors"
                    >
                      {t('عرض الكل', 'View All')}
                    </button>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="relative" ref={mailRef}>
                <button
                  onClick={() => { navigate('/messages'); }}
                  className="relative text-foreground/70 hover:text-foreground transition-all hover:scale-110"
                  title={t('الرسائل', 'Messages')}
                >
                  <Mail className="w-5 h-5" />
                  {unreadMsgCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-primary rounded-full text-[8px] text-white flex items-center justify-center font-bold">
                      {unreadMsgCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Language Toggle */}
              <button
                onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
                className="text-foreground/70 hover:text-foreground transition-all hover:scale-110"
                title={lang === 'ar' ? 'English' : 'عربي'}
              >
                <Globe className="w-5 h-5" />
              </button>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 bg-background overflow-x-hidden md:overflow-y-auto">
            {!isHome && (
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 m-4 mb-0 px-4 py-2 rounded-xl bg-primary/10 text-primary font-bold text-sm hover:bg-primary/20 transition-all"
              >
                <ArrowRight className="w-4 h-4" />
                {t('رجوع للرئيسية', 'Back to Home')}
              </button>
            )}
            {children}
          </main>
        </div>
        <DashboardSidebar />
      </div>
    </SidebarProvider>
  );
};

export default DashboardLayout;
